import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReadyState } from 'react-use-websocket';

import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import {
  getCachedInputLiveStatus,
  type InputLiveStatusSnapshot,
  isUnassignedLiveInputId,
  refreshInputLiveStatus,
  replayCachedInputLiveStatus,
  subscribeToInputLiveStatus,
  toSubscribedInputIds,
} from '@/services/inputStatus/live-input-status.service';
import { applicationDataSelector } from '@/store/slices/application-data';

export type { InputLiveStatusSnapshot } from '@/services/inputStatus/live-input-status.service';
export { fetchInputLiveStatusSnapshot, subscribeToInputLiveStatus } from '@/services/inputStatus/live-input-status.service';

/** Page-level subscribe + fetch + visibility catch-up for a set of input ids. */
export const useInputSourceLiveStatus = (inputIds: string[]) => {
  const { ouData } = useSelector(applicationDataSelector);
  const websocketContext = useWebSocketContext();
  const readyStateRef = useRef<number | undefined>(websocketContext?.readyState);
  readyStateRef.current = websocketContext?.readyState;

  const subscribedIds = toSubscribedInputIds(inputIds, ouData?.ouId);
  const subscribedKey = subscribedIds.join(',');
  const [liveStatusByInputId, setLiveStatusByInputId] = useState<Record<string, InputLiveStatusSnapshot>>({});

  useEffect(() => {
    const ids = subscribedKey ? subscribedKey.split(',') : [];
    const unsubscribers = ids.map((id) =>
      subscribeToInputLiveStatus(id, (status) => {
        setLiveStatusByInputId((prev) => ({ ...prev, [status.inputId]: status }));
      })
    );

    ids.forEach((id) => {
      refreshInputLiveStatus(id);
    });

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (readyStateRef.current === ReadyState.OPEN) {
        replayCachedInputLiveStatus(ids);
        return;
      }

      ids.forEach((id) => {
        refreshInputLiveStatus(id);
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [subscribedKey]);

  return {
    liveStatusByInputId,
    liveStatus: subscribedIds.length === 1 ? liveStatusByInputId[subscribedIds[0]] ?? null : null,
  };
};

/** Subscribe to cached live status without fetching. Use from portals on a page that already subscribed. */
export const useObservedInputLiveStatus = (inputId?: string): InputLiveStatusSnapshot | null => {
  const { ouData } = useSelector(applicationDataSelector);
  const ouId = ouData?.ouId;
  const observedInputId = isUnassignedLiveInputId(inputId, ouId) ? undefined : inputId;
  const [liveStatus, setLiveStatus] = useState<InputLiveStatusSnapshot | null>(() =>
    observedInputId ? getCachedInputLiveStatus(observedInputId) ?? null : null
  );

  useEffect(() => {
    if (!observedInputId) {
      setLiveStatus(null);
      return undefined;
    }

    return subscribeToInputLiveStatus(observedInputId, setLiveStatus);
  }, [observedInputId]);

  return liveStatus;
};
